<template>
  <!-- Single root div that will inherit all non-prop attributes -->
  <div v-bind="$attrs" :class="['favorite-button-wrapper', className]">
    <!-- Heart Button -->
    <div
      class="relative flex items-center text-gray-300 cursor-pointer"
      @click="handleFavoriteClick"
    >
      <!-- Heart Icon - Changes based on favorite status -->
      <div class="heart-icon-container">
        <!-- Filled heart when favorited -->
        <svg
          v-if="isFavorite"
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-teal-500 transition-transform duration-300"
          :class="favoriteAnimationClass"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
          />
        </svg>
        
        <!-- Outline heart when not favorited -->
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-gray-400 hover:text-teal-500 transition-colors duration-300"
          :class="favoriteAnimationClass"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
          />
        </svg>
      </div>
      <span v-if="showCount" class="ml-1">{{ favoritesCount }}</span>
    </div>

    <!-- Teleport the modal to body to fix positioning -->
    <Teleport to="body">
      <div 
        v-if="showLoginModal" 
        class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
        @click.self="showLoginModal = false"
      >
        <div class="bg-gray-800 rounded-lg p-5 shadow-xl w-full max-w-sm mx-4">
          <!-- Close Button -->
          <div class="flex justify-end mb-2">
            <button 
              @click="showLoginModal = false" 
              class="text-gray-400 hover:text-white focus:outline-none"
              aria-label="Close dialog"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
          
          <!-- Header -->
          <div class="text-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-12 w-12 text-teal-500 mx-auto mb-2"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              />
            </svg>
            <h3 class="text-lg font-bold text-white">Add to Favorites</h3>
            <p class="text-gray-300 text-sm mt-2">
              Create an account or log in to save your favorite AI tools and build a personalized collection.
            </p>
          </div>
          
          <!-- Actions -->
          <div class="flex gap-2">
            <Link 
              href="/login" 
              class="flex-1 bg-indigo-600 text-white rounded py-2 text-center text-sm hover:bg-indigo-700 transition-colors"
            >
              Login
            </Link>
            <Link 
              href="/register" 
              class="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded py-2 text-center text-sm hover:from-blue-600 hover:to-purple-600 transition-colors"
            >
              Register
            </Link>
          </div>
          <button 
            @click="showLoginModal = false" 
            class="w-full mt-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded py-2 text-center text-sm transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>



<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { usePage, Link } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
  toolId: {
    type: Number,
    required: true,
  },
  initialFavoritesCount: {
    type: Number,
    required: true,
  },
  initialIsFavorite: {
    type: Boolean,
    default: false,
  },
  className: {
    type: String,
    default: '',
  },
  showCount: {
    type: Boolean,
    default: true
  }
});

// Disable attribute inheritance warnings
defineOptions({
  inheritAttrs: false
});

const emit = defineEmits(['favoriteUpdated']);

const { auth } = usePage().props;
const showLoginModal = ref(false);
const favoritesCount = ref(props.initialFavoritesCount);
const isFavorite = ref(props.initialIsFavorite);
const favoriteAnimationClass = ref('');

// Update state when props change
watch(() => props.initialFavoritesCount, (newValue) => {
  favoritesCount.value = newValue;
});

watch(() => props.initialIsFavorite, (newValue) => {
  isFavorite.value = newValue;
});

// Handle escape key to close modal
const handleEscKey = (event) => {
  if (event.key === 'Escape' && showLoginModal.value) {
    showLoginModal.value = false;
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey);
});

const handleFavoriteClick = async () => {
  if (!auth.user) {
    showLoginModal.value = true;
    return;
  }
  
  try {
    const response = await axios.post(`/tools/${props.toolId}/favorite`);
    isFavorite.value = response.data.favorite;
    
    // Update favorites count
    const oldCount = favoritesCount.value;
    favoritesCount.value = isFavorite.value ? oldCount + 1 : Math.max(0, oldCount - 1);
    
    // Trigger animation
    favoriteAnimationClass.value = 'animate-exaggerate';
    setTimeout(() => {
      favoriteAnimationClass.value = '';
    }, 500);
    
    // Emit update event
    emit('favoriteUpdated', {
      toolId: props.toolId,
      isFavorite: isFavorite.value,
      favoritesCount: favoritesCount.value
    });
  } catch (error) {
    console.error('Failed to toggle favorite:', error);
  }
};
</script>



<style scoped>
.animate-exaggerate {
  transform: scale(1.5) rotate(10deg);
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.heart-icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>